import { Component } from "../base/Component";
import { ComponentFactory } from "../../service/ComponentFactory";
import { ITopic } from "../../models/ITopic";
import { PageContext } from "../../service/PageContext";
import { SpecialtySelect } from "../healioGlobal/SpecialtySelect";
import { EventUtil } from "../../util/EventUtil";

export class TopicFilter extends Component {
  public static readonly TopicFilterChangeEvent: string =
    "TopicFilterChangeEvent";

  private readonly _topicMenuContainer: HTMLElement | null;
  private readonly _topicMenuTemplate: HTMLElement | null;
  private readonly _topicChipsContainer: HTMLElement | null;
  private readonly _topicChipsTemplate: HTMLElement | null;
  private readonly _clearButton: HTMLElement | null;
  private readonly _applyButton: HTMLElement | null;

  private _lastSelectedValue: ReadonlyArray<string> = [];
  private _selectedTopics: ITopic[] = [];
  private _specialtyId: string = "";


  constructor(element: HTMLElement, context: PageContext) {
    super(element, context);

    this._topicMenuContainer = this._element.querySelector("[data-initialize-as='topic menu']");
    this._topicMenuTemplate = this._element.querySelector("[data-initialize-as='topic menu template']");
    this._topicChipsContainer = this._element.querySelector("[data-initialize-as='topic chips']");
    this._topicChipsTemplate = this._element.querySelector("[data-initialize-as='topic chips template']");
    this._clearButton = this._element.querySelector("[data-initialize-as='clear topics']");
    this._applyButton = this._element.querySelector("[data-initialize-as='apply topics']");

    document.addEventListener(SpecialtySelect.SpecialtySelectInitEvent, e => this.handleSpecialtyChange(e as CustomEvent<string>));

    if(this._applyButton){
      this._applyButton.addEventListener("click", ev => this.handleMenuApply(ev));
    }

    if(this._clearButton){
      this._clearButton.addEventListener("click", ev => this.handleClearAll(ev));
    }

    $(this._element).find(".dropdown-menu").on("click.bs.dropdown", function (e) {
      e.stopPropagation();
    });
  }

  public get topics(): ReadonlyArray<ITopic> {
    return this._selectedTopics;
  }

  private async handleSpecialtyChange(
    event: CustomEvent<string>
  ): Promise<void> {
    if (event.cancelable) {
      event.preventDefault();
      event.stopPropagation();
    }

    this._specialtyId = event.detail;
    const context = this._pageContext;
    const service = context ? context.topicListService : null;

    if (service === null) {
      throw new Error("No topic service available.");
    }

    const topics = await service.getTopicsBySpecialty(this._specialtyId);

    const cookieValue = this.getCookie("healio.topicfilter" + this._specialtyId);
    let selectedTopics: ITopic[] = [];
    if (cookieValue){
      selectedTopics = JSON.parse(decodeURIComponent(cookieValue)) as ITopic[];
    }

    if (topics.length > 0){
      this._element.hidden = false;
    }

    this.updateTopics(topics, selectedTopics);
  }

  private raiseChangeEvent(specialtyChangeEvent?: CustomEvent<string>): void {
    // The "Topic Change" event fires when the filter has detected a change in
    // internal state, such as due to the user clicking on chips or hitting
    // the apply button.  However, it can also fire if a specialty change
    // event is being chained in, even if this event does not result in a
    // change in internal state.  This is because items listening for this
    // event are generally concerned about the combined state of the two
    // filters, and if the specialty filter has changed, then the context of
    // the topic filter has changed, even if the internal state has not.
    // In other words, there is a contextual change between
    // "specialty 1, no topics selected" and "specialty 2, no topics selected",
    // and we need to signal that context shift, even though nothing internal
    // has actually changed.

    const newValue = this._selectedTopics;
    const ids = newValue.map(t => t.topicId);

    if (!specialtyChangeEvent) {
      const sameAsLastTime =
        ids.length != 0 &&
        this._lastSelectedValue.length != 0 &&
        ids.length === this._lastSelectedValue.length &&
        ids.every(id => this._lastSelectedValue.indexOf(id) !== -1);

      if (sameAsLastTime) {
        return;
      }
    }

    this._lastSelectedValue = ids;

    this._element.dispatchEvent(EventUtil.createCustomEvent(TopicFilter.TopicFilterChangeEvent, newValue));
  }

  private showHideClearFilter(): void {
    if (this._clearButton) {
      this._clearButton.hidden = this._selectedTopics.length > 0
        ? false
        : true;
    }
  }

  public handleMenuApply(ev: Event | null): void {

    if(!this._topicMenuContainer){
      return;
    }

    this._selectedTopics = [];
    this._topicMenuContainer.querySelectorAll<HTMLInputElement>("input[type='checkbox']:checked").forEach(topic => {
      if(topic.dataset.topicName && topic.dataset.topicId){
        this._selectedTopics.push({topicId: topic.dataset.topicId, topicName: topic.dataset.topicName});
      }
    });

    this.setCookie();

    this.updateChips();

    this.showHideClearFilter();

    this.raiseChangeEvent();
  }

  private handleClearAll(ev: Event | null) : void {
    if (ev?.cancelable) {
      ev.preventDefault();
      ev.stopPropagation();
    }
    this._selectedTopics = [];

    this.setCookie();

    this.updateChips();

    this.clearCheckboxes();

    this.showHideClearFilter();

    this.raiseChangeEvent();
  }

  private updateChips(): void {
    if(this._topicChipsTemplate && this._topicChipsContainer){

      this._topicChipsContainer.innerHTML = "";

      for(var topic of this._selectedTopics){
        let html = this._topicChipsTemplate.innerHTML
          .replace("{{topicId}}", topic.topicId)
          .replace("{{topicName}}", topic.topicName)
          .replace("{{topicLabel}}", topic.topicName);
        this._topicChipsContainer.innerHTML += html;
      }

      this._topicChipsContainer.querySelectorAll("button").forEach(chip => {
        chip.addEventListener("click", e => this.removeChip(chip.dataset.topicId ?? ""));
      });
    }
  }

  private clearCheckboxes(): void {
    this._topicMenuContainer?.querySelectorAll('input[data-topic-id]').forEach(el => {
      (el as HTMLInputElement).checked = false;
    });
  }

  private removeChip(topicId: string): void {
    if(!this._topicMenuContainer){
      return;
    }

    let chipMenuItem = this._topicMenuContainer.querySelector("input[data-topic-id='" + topicId + "']") as HTMLInputElement;
    if(chipMenuItem){
      chipMenuItem.checked = false;

      this.handleMenuApply(null);
    }
  }

  private updateTopics(topics: ITopic[], selectedTopics: ITopic[]){
    let selectedTopicIds = selectedTopics.map(t => t.topicId);
    if(this._topicMenuContainer && this._topicMenuTemplate){
      for (var topic of topics){
        let html = this._topicMenuTemplate.innerHTML
          .replace("{{topicId}}", topic.topicId)
          .replace("{{topicName}}", topic.topicName)
          .replace("{{topicLabel}}", topic.topicName);
        html.replace("{{checked}}", selectedTopicIds.includes(topic.topicId) ? "checked" : "");

        this._topicMenuContainer.innerHTML += html;
      }
    }

    this.handleMenuApply(null);
  }

  private setCookie() : void {
    if (window.location.pathname){
      const filterValue = JSON.stringify(this._selectedTopics);
      document.cookie = "healio.topicfilter." + this._specialtyId + "=" + encodeURIComponent(filterValue);
    }
  }

  private getCookie(cname: string): string {
    const name = cname + "=";
    const ca = document.cookie.split(";");

    for(let c of ca) {
      while (c.charAt(0) === " ") {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }

    return "";
  }
}

ComponentFactory.registerComponent("TopicFilter", TopicFilter);
